import ReactGA from 'react-ga'
import TagManager from 'react-gtm-module'
import moment from 'moment'
import posthogJs from 'posthog-js'
import eventConstants from './eventConstants'

const isLikelyUrl = value => {
  if (typeof value !== 'string' || !value) {
    return false
  }

  return (
    /^https?:\/\//i.test(value) ||
    value.startsWith('//') ||
    value.startsWith('/')
  )
}

const stripUrlParams = value => {
  if (!isLikelyUrl(value)) {
    return value
  }

  // Remove everything from the first query/hash marker.
  return value.replace(/[?#].*$/, '')
}

const sanitizeUrlValues = value => {
  if (Array.isArray(value)) {
    return value.map(item => sanitizeUrlValues(item))
  }

  if (value && typeof value === 'object') {
    const sanitizedObject = {}
    Object.keys(value).forEach(key => {
      sanitizedObject[key] = sanitizeUrlValues(value[key])
    })
    return sanitizedObject
  }

  return stripUrlParams(value)
}

const sanitizePosthogEvent = event => {
  if (!event?.properties) {
    return event
  }

  event.properties = sanitizeUrlValues(event.properties)
  return event
}

/* eslint-disable */
export const initializeEventTracking = async function() {
  try {
    const HEAP_APP_ID =
      window.location && window.location.hostname === 'unroll.me'
        ? '3543597151'
        : '821966953'
    window.heap.load(HEAP_APP_ID, {
      disableTextCapture: false,
      secureCookie: true
    })
  } catch (err) {
    console.error('Heap initialization error', err)
  }

  try {
    window.posthog = posthogJs.init(process.env.REACT_APP_POSTHOG_KEY, {
      api_host: process.env.REACT_APP_POSTHOG_HOST,
      person_profiles: 'identified_only',
      before_send: sanitizePosthogEvent,
      autocapture: false
    })

    // Wrap identify to sanitize user properties and remove PII.
    const originalIdentify = window.posthog.identify
    window.posthog.identify = function(
      distinctId,
      userProperties = {},
      options = {}
    ) {
      const sanitizedProperties = sanitizeUrlValues(userProperties)
      return originalIdentify.call(
        this,
        distinctId,
        sanitizedProperties,
        options
      )
    }
  } catch (err) {
    console.error('Posthog initialization error', err)
  }

  ReactGA.initialize('UA-212332778-1', {
    titleCase: false
  })

  ReactGA.pageview()

  TagManager.initialize({
    gtmId: 'GTM-WHZJ9L3'
  })
}
/* eslint-disable */

let state

const normalizeProvider = providerValue => {
  if (!providerValue) {
    return null
  }

  // If the provider is already a label (capitalized), return as is
  if (
    typeof providerValue === 'string' &&
    providerValue[0] === providerValue[0].toUpperCase()
  ) {
    return providerValue
  }

  // Try to get the label from eventConstants
  const providerLabel = eventConstants.common.providerLabel[providerValue]
  return providerLabel || providerValue
}

export const track = (eventName, data = {}) => {
  // Try to get provider from Redux store if not provided in data
  let provider = data.provider

  // First, normalize the provider if it was passed
  if (provider) {
    provider = normalizeProvider(provider)
  }

  // If no provider provided, try to get it from Redux store
  if (!provider && typeof window !== 'undefined') {
    try {
      // Dynamically import store to avoid circular dependencies
      const store = require('../redux/store').default
      const currentState = store.getState()
      const user = currentState?.app?.user

      if (user && user.service_provider) {
        // Get the provider label from eventConstants
        provider = normalizeProvider(user.service_provider)
      }
    } catch (err) {
      // Silently fail if store is not available
    }
  }
  const safeData = sanitizeUrlValues({
    ...data,
    Platform: 'Web',
    ...(provider && { provider })
  })
  try {
    window.heap.track(eventName, safeData)
  } catch (err) {
    console.error('Heap tracking error', err)
  }

  try {
    console.warn('posthog track', eventName, safeData)
    window.posthog.capture(eventName, safeData)
  } catch (err) {
    console.error('Posthog tracking error', err)
  }

  try {
    ReactGA.event({
      category: 'All',
      action: eventName.toLowerCase()
    })
  } catch (err) {
    console.error('ReactGA tracking error', err)
  }
}

const buildPosthogDistinctId = email => {
  const normalizedEmail = (email || '').trim().toLowerCase()
  if (!normalizedEmail) {
    return null
  }

  // Deterministic non-DB identifier for PostHog distinct_id.
  let hash = 5381
  for (let i = 0; i < normalizedEmail.length; i++) {
    hash = (hash * 33) ^ normalizedEmail.charCodeAt(i)
  }

  return `web_${(hash >>> 0).toString(36)}`
}

const buildPosthogDisplayName = distinctId => {
  if (!distinctId) {
    return null
  }

  return `user_${distinctId.replace(/^web_/, '')}`
}

export function IdentifyPostHogUser(email) {
  const normalizedEmail = (email || '').trim().toLowerCase()
  const distinctId = buildPosthogDistinctId(normalizedEmail)
  const anonymizedDisplayName = buildPosthogDisplayName(distinctId)

  if (!distinctId || !anonymizedDisplayName || !window.posthog?.identify) {
    return
  }

  window.posthog.identify(distinctId, {
    name: anonymizedDisplayName,
    person_display_name: anonymizedDisplayName
  })
}

export function IdentifyUser({
  id,
  service_provider,
  auth_type,
  signup_date,
  settings
}) {
  const userProperties = {
    [eventConstants.userProperties.hasAppPassword]: auth_type === 'password',
    [eventConstants.userProperties.signupDate]: signup_date
      ? moment(signup_date).format('YYYY/MM/DD')
      : '',
    ID: id,
    [eventConstants.userProperties.provider]:
      eventConstants.common.providerLabel[service_provider],
    [eventConstants.userProperties.researchOptOut]:
      settings.is_opted_out_of_data_sharing,
    [eventConstants.userProperties.adOptOut]:
      settings.is_opted_out_of_ad_targeting
  }

  window.heap.identify(id)
  window.heap.addUserProperties(userProperties)
}

const subscriptionUpdated = action => {
  return {
    currentState: action.payload.currentFilter,
    transitionState: action.payload.filter,
    inGroup: !!action.payload.inGroup,
    editAsGroup: action.payload.inGroup === 'groupEdit',
    totalInGroup: action.payload.groupSize,
    subscriptionId: action.payload.subscriptionId,
    groupedSubscriptionIds: action.payload.inGroupIds
  }
}

const trackingData = {
  UPDATE_SUBSCRIPTION_STATE: {
    event: 'subscription_action',
    cb: subscriptionUpdated
  }
}

/* eslint-disable */
const eventTrackingMiddleware = store => next => action => {
  state = store.getState()
  if (action.type === 'LOGOUT_USER') {
    state = null
  }

  if (trackingData[action.type]) {
    if (typeof trackingData[action.type] === 'function') {
      trackingData[action.type](state, action)
    } else {
      track(
        trackingData[action.type].event,
        trackingData[action.type].cb(action)
      )
    }
  }
  return next(action)
}
/* eslint-disable */

export default eventTrackingMiddleware
